import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { PageHeader, Loading, EmptyState } from "@/components/ui-helpers";
import { ArchiveDialog } from "@/components/ArchiveDialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { RotateCcw, Trash2, Trash } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { key: "orders", label: "Order" },
  { key: "trips", label: "Trip" },
  { key: "purchases", label: "Pembelian" },
  { key: "payments", label: "Pembayaran" },
  { key: "expenses", label: "Pengeluaran" },
];

export default function RecycleBin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("orders");
  const [selected, setSelected] = useState({});
  const [confirm, setConfirm] = useState(null); // {mode:'one'|'bulk', ids:[]}

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/recycle-bin"); setData(r.data); setSelected({}); }
    catch { toast.error("Gagal memuat kotak sampah"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const records = data?.records?.[tab] || [];
  const counts = data?.counts || {};
  const selIds = Object.keys(selected).filter((k) => selected[k]);

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleAll = () => {
    if (selIds.length === records.length) setSelected({});
    else setSelected(Object.fromEntries(records.map((r) => [r.id, true])));
  };

  const restore = async (id) => {
    try { await api.post(`/recycle-bin/${tab}/${id}/restore`); toast.success("Dipulihkan"); load(); }
    catch (e) { toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Gagal memulihkan"); }
  };

  const bulkRestore = async () => {
    if (selIds.length === 0) return toast.error("Pilih record dulu");
    try {
      const r = await api.post("/recycle-bin/bulk", { type: tab, action: "restore", ids: selIds });
      toast.success(`${r.data.success.length} record dipulihkan`);
      load();
    } catch { toast.error("Gagal memulihkan"); }
  };

  const doPermanent = async () => {
    if (!confirm) return;
    if (confirm.mode === "one") {
      try { await api.delete(`/recycle-bin/${tab}/${confirm.ids[0]}`); toast.success("Dihapus permanen"); load(); }
      catch (e) { toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Gagal menghapus"); }
    } else {
      try {
        const r = await api.post("/recycle-bin/bulk", { type: tab, action: "permanent_delete", ids: confirm.ids });
        toast.success(`${r.data.success.length} dihapus permanen`);
        if (r.data.blocked?.length) toast.warning(`${r.data.blocked.length} tidak bisa dihapus (ada dependensi)`);
        load();
      } catch { toast.error("Gagal menghapus"); }
    }
  };

  return (
    <div>
      <PageHeader title="Kotak Sampah" subtitle="Pulihkan atau hapus permanen record yang diarsipkan" />

      {loading ? <Loading /> : (
        <Tabs value={tab} onValueChange={(v) => { setTab(v); setSelected({}); }}>
          <TabsList className="flex-wrap h-auto">
            {TYPES.map((t) => (
              <TabsTrigger key={t.key} value={t.key} data-testid={`bin-tab-${t.key}`}>
                {t.label} <span className="ml-1.5 text-xs opacity-70">({counts[t.key] || 0})</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {TYPES.map((t) => (
            <TabsContent key={t.key} value={t.key} className="mt-4">
              {records.length === 0 ? (
                <EmptyState title={`Tidak ada ${t.label} terarsip`} subtitle="Record yang diarsipkan akan muncul di sini." />
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-3 flex-wrap no-print">
                    <div className="flex items-center gap-2 mr-auto">
                      <Checkbox checked={selIds.length === records.length && records.length > 0} onCheckedChange={toggleAll} data-testid="bin-select-all" />
                      <span className="text-sm text-muted-foreground">{selIds.length} dipilih</span>
                    </div>
                    <Button size="sm" variant="outline" disabled={selIds.length === 0} onClick={bulkRestore} data-testid="bin-bulk-restore"><RotateCcw className="h-3.5 w-3.5 mr-1.5" />Pulihkan Terpilih</Button>
                    <Button size="sm" variant="outline" className="text-destructive" disabled={selIds.length === 0} onClick={() => setConfirm({ mode: "bulk", ids: selIds })} data-testid="bin-bulk-delete"><Trash2 className="h-3.5 w-3.5 mr-1.5" />Hapus Permanen Terpilih</Button>
                  </div>
                  <div className="rounded-xl border border-border bg-card overflow-hidden">
                    {records.map((r) => (
                      <div key={r.id} className="flex items-center gap-3 p-3.5 border-b border-border last:border-0" data-testid="bin-record">
                        <Checkbox checked={!!selected[r.id]} onCheckedChange={() => toggle(r.id)} data-testid="bin-record-check" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{r._label}</p>
                          <p className="text-xs text-muted-foreground">Diarsipkan {formatDateTime(r.deleted_at)} oleh {r.deleted_by || "-"}</p>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => restore(r.id)} data-testid="bin-restore"><RotateCcw className="h-3.5 w-3.5 mr-1" />Pulihkan</Button>
                        <Button size="sm" variant="outline" className="text-destructive" onClick={() => setConfirm({ mode: "one", ids: [r.id] })} data-testid="bin-permanent-delete"><Trash className="h-3.5 w-3.5" /></Button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </TabsContent>
          ))}
        </Tabs>
      )}

      <ArchiveDialog
        open={!!confirm}
        onOpenChange={(v) => { if (!v) setConfirm(null); }}
        title="Hapus Permanen?"
        description={confirm?.mode === "bulk"
          ? `${confirm.ids.length} record akan dihapus PERMANEN dan tidak dapat dipulihkan. Record dengan dependensi aktif akan dilewati.`
          : "Record ini akan dihapus PERMANEN dari database dan tidak dapat dipulihkan."}
        confirmLabel="Hapus Permanen"
        destructive
        onConfirm={doPermanent}
        testId="bin-perm"
      />
    </div>
  );
}
